import { useSearchParams } from 'react-router-dom';
import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useMasters } from '../../hooks/useMasters';
import { usePermission } from '../../hooks/usePermission';
import { fine, money, weight as fmtWeight } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { PieceDrawer, type Piece } from './PieceDrawer';
import { useLocations } from '../../hooks/useLocations';
import { Drawer, Field, NumberInput, Select, TextInput } from '../../components/ui/Form';

interface Item {
  id: string;
  item_code: string;
  barcode: string | null;
  status: string;
  product_name: string;
  purity_code: string;
  branch_id: string;
  branch_name: string;
  image_data_uri?: string | null;
  has_own_photo?: boolean;
  location_id?: string | null;
  location_name?: string | null;
  gross_wt: string;
  net_wt: string;
  fine_wt: string;
  acquisition_cost: string | null;
}

const STATUS_TONE: Record<string, string> = {
  IN_STOCK: 'text-success',
  SOLD: 'text-ink-soft',
  AT_JOBWORK: 'text-gold-700',
  IN_TRANSIT: 'text-gold-700',
  DAMAGED: 'text-danger',
  LOST: 'text-danger',
  MELTED: 'text-ink-soft',
};

const BLANK = {
  productId: '',
  purityId: '',
  itemCode: '',
  barcode: '',
  grossWt: '',
  stoneWt: '',
  acquisitionCost: '',
};

export function ItemsPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const masters = useMasters();
  const { branches, defaultBranchId } = useBranches();

  const [status, setStatus] = useState('IN_STOCK');
  const [search, setSearch] = useState('');
  const [branchId, setBranchId] = useState('');
  // Opened from a location card, the list starts filtered to that showcase.
  const [params] = useSearchParams();
  const [locationId, setLocationId] = useState(params.get('locationId') ?? '');
  const [viewing, setViewing] = useState<Piece | null>(null);
  const { locations } = useLocations(branchId || null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...BLANK });
  const [error, setError] = useState<unknown>(null);

  const products = useQuery({ queryKey: ['products', ''], queryFn: () => endpoints.masters.products() });
  const items = useQuery({
    queryKey: ['items', status, search, branchId, locationId],
    queryFn: () =>
      endpoints.inventory.items({
        status: status || undefined,
        q: search || undefined,
        branchId: branchId || undefined,
        locationId: locationId || undefined,
      }),
  });

  const productList = (products.data?.data ?? []) as unknown as { id: string; sku: string; name: string; metal_id: string }[];

  /** A piece's purity must belong to its product's metal, so the list narrows. */
  const purityOptions = useMemo(() => {
    const product = productList.find((p) => p.id === form.productId);
    if (!product) return [];
    return masters.puritiesFor(product.metal_id).map((p) => ({ value: p.id, label: p.code }));
  }, [form.productId, productList, masters]);

  const create = useMutation({
    mutationFn: () =>
      api.post('/items', {
        branchId: branchId || defaultBranchId,
        productId: form.productId,
        purityId: form.purityId,
        itemCode: form.itemCode,
        ...(form.barcode ? { barcode: form.barcode } : {}),
        grossWt: form.grossWt,
        ...(form.stoneWt ? { stoneWt: form.stoneWt } : {}),
        acquisitionCost: form.acquisitionCost,
      }),
    onSuccess: () => {
      setOpen(false);
      setForm({ ...BLANK });
      setError(null);
      void client.invalidateQueries({ queryKey: ['items'] });
    },
    onError: setError,
  });

  const rows = (items.data?.data ?? []) as unknown as Item[];
  const totalFine = rows.reduce((a, r) => a + Number(r.fine_wt), 0);

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    create.mutate();
  };

  const ready =
    form.productId !== '' && form.purityId !== '' && form.itemCode.trim() !== '' && form.grossWt !== '' && form.acquisitionCost !== '';

  return (
    <>
      <PageHeader
        title="Items"
        subtitle={rows.length > 0 ? `${rows.length} pieces, ${fine(totalFine.toFixed(4))} fine` : 'Every tagged piece in the shop'}
        actions={
          <>
            <input
              className="field w-52"
              placeholder="Tag, barcode or product"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select className="field w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="IN_STOCK">In stock</option>
              <option value="SOLD">Sold</option>
              <option value="AT_JOBWORK">At job work</option>
              <option value="IN_TRANSIT">In transit</option>
              <option value="DAMAGED">Damaged</option>
            </select>
            {branches.length > 1 ? (
              <select className="field w-40" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">All branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            ) : null}
            {locations.length > 0 ? (
              <select className="field w-40" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                <option value="">All locations</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            ) : null}
            {can('item.create') ? (
              <button className="btn-primary whitespace-nowrap" onClick={() => setOpen(true)}>
                Tag piece
              </button>
            ) : null}
          </>
        }
      />

      {items.isLoading ? <Loading /> : null}

      {!items.isLoading && rows.length === 0 ? (
        <Empty
          message="No pieces here yet."
          hint="Tag a piece to put it on the shelf, or bring your opening stock in through a purchase."
          action={can('item.create') ? { label: 'Tag piece', onClick: () => setOpen(true) } : undefined}
        />
      ) : null}

      {rows.length > 0 ? (
        <Table
          head={[
            '',
            'Item',
            'Product',
            'Purity',
            'Location',
            'Gross',
            'Net',
            'Fine',
            ...(can('stock.view_value') ? ['Cost'] : []),
            'Status',
          ]}
        >
          {rows.map((i) => (
            <tr key={i.id} className="cursor-pointer hover:bg-gold-50" onClick={() => setViewing(i)}>
              <td className="td">
                <div className="h-10 w-10 overflow-hidden rounded border border-silver-100 bg-silver-50">
                  {i.image_data_uri ? (
                    <img src={i.image_data_uri} alt="" className={`h-full w-full object-cover ${i.has_own_photo ? '' : 'opacity-60'}`} />
                  ) : null}
                </div>
              </td>
              <td className="td num">
                {i.item_code}
                {i.barcode ? <span className="block text-xs text-ink-soft">{i.barcode}</span> : null}
              </td>
              <td className="td">{i.product_name}</td>
              <td className="td">{i.purity_code}</td>
              <td className="td">
                {i.location_name ? (
                  i.location_name
                ) : i.status === 'IN_STOCK' ? (
                  <span className="text-2xs text-warn">not placed</span>
                ) : (
                  <span className="text-ink-faint">—</span>
                )}
                {branches.length > 1 ? <span className="block text-2xs text-ink-faint">{i.branch_name}</span> : null}
              </td>
              <td className="td num text-right">{fmtWeight(i.gross_wt)}</td>
              <td className="td num text-right">{fmtWeight(i.net_wt)}</td>
              <td className="td num text-right">{fine(i.fine_wt)}</td>
              {can('stock.view_value') ? <td className="td num text-right">{money(i.acquisition_cost)}</td> : null}
              <td className={`td ${STATUS_TONE[i.status] ?? ''}`}>{i.status.replace(/_/g, ' ').toLowerCase()}</td>
            </tr>
          ))}
        </Table>
      ) : null}

      {viewing ? <PieceDrawer piece={viewing} onClose={() => setViewing(null)} /> : null}

      {open ? (
        <Drawer
          title="Tag a piece"
          subtitle="Net and fine weight are worked out by the server from what you enter, using the same engine that prices the sale."
          onClose={() => setOpen(false)}
        >
          <ErrorNotice error={error} />
          <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-4">
            <Field label="Product" required>
              <Select
                value={form.productId}
                onChange={(v) => setForm({ ...form, productId: v, purityId: '' })}
                options={productList.map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }))}
                placeholder={productList.length === 0 ? 'Add a product first' : 'Choose…'}
              />
            </Field>

            <Field label="Purity" required>
              <Select
                value={form.purityId}
                onChange={(v) => setForm({ ...form, purityId: v })}
                options={purityOptions}
                placeholder={form.productId ? 'Choose…' : 'Choose a product first'}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Item code" required hint="Your tag number.">
                <TextInput
                  value={form.itemCode}
                  onChange={(v) => setForm({ ...form, itemCode: v })}
                  placeholder="BNG-0001"
                  maxLength={64}
                  uppercase
                  allow={/[A-Z0-9\-_/]/}
                />
              </Field>
              <Field label="Barcode" hint="What the scanner reads.">
                <TextInput
                  value={form.barcode}
                  onChange={(v) => setForm({ ...form, barcode: v })}
                  maxLength={64}
                  allow={/[A-Za-z0-9\-_]/}
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Gross weight" required>
                <NumberInput value={form.grossWt} onChange={(v) => setForm({ ...form, grossWt: v })} placeholder="10.000" suffix="g" decimals={3} />
              </Field>
              <Field label="Stone weight" hint="Deducted from gross to get net.">
                <NumberInput value={form.stoneWt} onChange={(v) => setForm({ ...form, stoneWt: v })} placeholder="0.000" suffix="g" decimals={3} />
              </Field>
            </div>

            <Field
              label="Cost"
              required
              hint="What this piece cost you. Profit is measured against this figure and it never changes with the rate."
            >
              <NumberInput
                value={form.acquisitionCost}
                onChange={(v) => setForm({ ...form, acquisitionCost: v })}
                placeholder="62000.00"
                suffix="₹"
              />
            </Field>

            <div className="flex gap-2 pt-2">
              <button className="btn-primary" disabled={!ready || create.isPending}>
                {create.isPending ? 'Saving…' : 'Tag piece'}
              </button>
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Drawer>
      ) : null}
    </>
  );
}
